import React, { useEffect, useState, type FC } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import axios, { type AxiosResponse } from 'axios';

import { NavLinkButton, Panel } from '@shared/components/bootstrap';

import { Permission } from '@admin/components/helpers/permission';
import { HistoriesPanel } from '@admin/components/histories/panel';
import { NotesPanel } from '@admin/components/notes/panel';
import { useAxiosRequest } from '@admin/hooks';
import { useNavigate } from '@admin/hooks/use_navigate';
import { useTitle } from '@admin/hooks/use_title';
import { MARKETING } from '@admin/libraries/access_control_catalog.generated';
import { History__Subject, NoteNotableEnum } from '@admin/schema';

import { AffiliateDeleteButton } from './delete_button';

type Affiliate = {
  id: number;
  name: string;
  email: string;
  phone: string;
  payout: number;
  discount: number;
};

const useAffiliateRequest = (id: string) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<Affiliate>> | undefined>(undefined);

  useEffect(() => {
    setRequest(axios.get<Affiliate>(`/marketing/affiliates/${id}`));
  }, [id]);

  return useAxiosRequest(request);
};

export const AffiliateDetails: FC = () => {
  useTitle('View Affiliate');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { response } = useAffiliateRequest(id);
  if (!response) return null;
  const affiliate = response.data;

  return (
    <>
      <h1 className="page-header text-overflow">
        <ol className="breadcrumb">
          <li>
            <NavLink to="/marketing/affiliates">Affiliates</NavLink>
          </li>
          <li className="active">{affiliate?.name ?? '-'}</li>
        </ol>
      </h1>

      <Panel>
        <Panel.Header>
          <Panel.Title>Details</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <p>
            <strong>Email:</strong>
            <span>{affiliate.email}</span>
          </p>
          <p>
            <strong>Phone #:</strong>
            <span>{affiliate.phone}</span>
          </p>
          <p>
            <strong>Payout:</strong>
            <span>${affiliate.payout?.toFixed(2) ?? '-'}</span>
          </p>
          <p>
            <strong>Discount:</strong>
            <span>${affiliate.discount?.toFixed(2) ?? '-'}</span>
          </p>
        </Panel.Body>
        <Panel.Footer align="right">
          <NavLinkButton to="/admin/affiliates" kind="secondary">
            Back
          </NavLinkButton>
          <Permission resource={MARKETING.resource} action={MARKETING.actions.update} denied={null}>
            <NavLinkButton to={`/admin/affiliates/${affiliate.id}/edit`} kind="primary">
              Edit
            </NavLinkButton>
            <AffiliateDeleteButton affiliate={affiliate} onDelete={() => navigate('/marketing/affiliates')} />
          </Permission>
        </Panel.Footer>
      </Panel>

      <NotesPanel type={NoteNotableEnum.Affiliate} id={String(affiliate.id)} />

      <HistoriesPanel type={History__Subject.Affiliate} id={String(affiliate.id)} />
    </>
  );
};
